<div class="policy-details-container">
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="spinner-border text-primary" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <p class="mt-3 text-muted">Loading policy details...</p>
  </div>

  <!-- Policy Details Content -->
  <div *ngIf="!loading && policyDetails" class="policy-details-content">
    <!-- Header Section -->
    <div class="policy-header">
      <div class="policy-title-section">
        <div class="resource-type-badge" [ngClass]="getResourceTypeBadgeClass()">
          <i [class]="getResourceTypeIcon()"></i>
          {{ policyDetails.ResourceType }}
        </div>
        <h4 class="policy-name">{{ policyDetails.PolicyName }}</h4>
        <p class="policy-description" *ngIf="policyDetails.Description">{{ policyDetails.Description }}</p>
      </div>
    </div>

    <!-- Policy Sections -->
    <div class="policy-sections">
      
      <!-- Creation/Create Options (for VM use CreationPolicyOptions, for Subscription use CreateOptions) -->
      <div class="policy-section" *ngIf="hasCreationPolicyOptions() || hasCreateOptions()">
        <h5 class="section-title">
          <i class="fas fa-play-circle"></i>
          {{ isVMPolicy() ? 'Creation Policy' : 'Create Options' }}
        </h5>
        <div class="options-grid">
          <!-- VM Creation Policy Options -->
          <div *ngIf="hasCreationPolicyOptions()">
            <div class="option-card active-option" 
                 *ngFor="let option of getActiveOptions(policyDetails.CreationPolicyOptions || [])">
              <div class="option-header">
                <span class="option-status status-active">
                  <i class="fas fa-check-circle"></i>
                  Active
                </span>
                <span class="option-name">{{ option.optionName }}</span>
              </div>
              <p class="option-description">{{ processDescription(option.description, option.parVal, option.parUnit) }}</p>
              <div class="option-params" *ngIf="option.parVal && option.parUnit">
                <span class="param-badge">{{ option.parVal }} {{ option.parUnit }}</span>
              </div>
            </div>
          </div>

          <!-- Subscription Create Options -->
          <div *ngIf="hasCreateOptions()">
            <div class="option-card active-option" 
                 *ngFor="let option of getActiveOptions(policyDetails.CreateOptions || [])">
              <div class="option-header">
                <span class="option-status status-active">
                  <i class="fas fa-check-circle"></i>
                  Active
                </span>
                <span class="option-name">{{ option.optionName }}</span>
              </div>
              <p class="option-description">{{ processDescription(option.description, option.parVal, option.parUnit) }}</p>
              <div class="option-params" *ngIf="option.parVal && option.parUnit">
                <span class="param-badge">{{ option.parVal }} {{ option.parUnit }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily Deletion Policy -->
      <div class="policy-section" *ngIf="hasDailyDeletionOptions()">
        <h5 class="section-title">
          <i class="fas fa-calendar-day"></i>
          Daily Deletion Policy
        </h5>
        <div class="options-grid">
          <div class="option-card active-option" 
               *ngFor="let option of getActiveOptions(policyDetails.DailyDeletionPolicyOptions || [])">
            <div class="option-header">
              <span class="option-status status-active">
                <i class="fas fa-check-circle"></i>
                Active
              </span>
              <span class="option-name">{{ option.optionName }}</span>
            </div>
            <p class="option-description">{{ processDescription(option.description, option.parVal, option.parUnit) }}</p>
            <div class="option-params" *ngIf="option.parVal && option.parUnit">
              <span class="param-badge">{{ option.parVal }} {{ option.parUnit }}</span>
            </div>
            <!-- Resources to be deleted (for VM policies) -->
            <div *ngIf="isVMPolicy() && option.resourcesToBeDeleted && option.resourcesToBeDeleted.length > 0" class="resources-section">
              <h6 class="resources-title">Resources to be deleted:</h6>
              <div class="resources-list">
                <span class="resource-badge deleted" *ngFor="let resource of option.resourcesToBeDeleted">
                  <i class="fas fa-trash"></i>
                  {{ resource }}
                </span>
              </div>
            </div>
            <!-- All resources (for reference) -->
            <div *ngIf="isVMPolicy() && option.allResources && option.allResources.length > 0" class="resources-section">
              <h6 class="resources-title">All resources:</h6>
              <div class="resources-list">
                <span class="resource-badge" 
                      *ngFor="let resource of option.allResources"
                      [class.deleted]="option.resourcesToBeDeleted?.includes(resource)"
                      [class.preserved]="!option.resourcesToBeDeleted?.includes(resource)">
                  <i class="fas" [class.fa-trash]="option.resourcesToBeDeleted?.includes(resource)" 
                     [class.fa-shield-alt]="!option.resourcesToBeDeleted?.includes(resource)"></i>
                  {{ resource }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Class End Deletion Policy -->
      <div class="policy-section" *ngIf="hasClassEndDeletionOptions()">
        <h5 class="section-title">
          <i class="fas fa-stop-circle"></i>
          Class End Deletion Policy
        </h5>
        <div class="options-grid">
          <div class="option-card active-option" 
               *ngFor="let option of getActiveOptions(policyDetails.ClassEndDeletionOptions || [])">
            <div class="option-header">
              <span class="option-status status-active">
                <i class="fas fa-check-circle"></i>
                Active
              </span>
              <span class="option-name">{{ option.optionName }}</span>
            </div>
            <p class="option-description">{{ processDescription(option.description, option.parVal, option.parUnit) }}</p>
            <div class="option-params" *ngIf="option.parVal && option.parUnit">
              <span class="param-badge">{{ option.parVal }} {{ option.parUnit }}</span>
            </div>
            <!-- Resources to be deleted (for VM policies) -->
            <div *ngIf="isVMPolicy() && option.resourcesToBeDeleted && option.resourcesToBeDeleted.length > 0" class="resources-section">
              <h6 class="resources-title">Resources to be deleted:</h6>
              <div class="resources-list">
                <span class="resource-badge deleted" *ngFor="let resource of option.resourcesToBeDeleted">
                  <i class="fas fa-trash"></i>
                  {{ resource }}
                </span>
              </div>
            </div>
            <!-- All resources (for reference) -->
            <div *ngIf="isVMPolicy() && option.allResources && option.allResources.length > 0" class="resources-section">
              <h6 class="resources-title">All resources:</h6>
              <div class="resources-list">
                <span class="resource-badge" 
                      *ngFor="let resource of option.allResources"
                      [class.deleted]="option.resourcesToBeDeleted?.includes(resource)"
                      [class.preserved]="!option.resourcesToBeDeleted?.includes(resource)">
                  <i class="fas" [class.fa-trash]="option.resourcesToBeDeleted?.includes(resource)" 
                     [class.fa-shield-alt]="!option.resourcesToBeDeleted?.includes(resource)"></i>
                  {{ resource }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quota Reached Policy -->
      <div class="policy-section" *ngIf="hasQuotaReachedOptions()">
        <h5 class="section-title">
          <i class="fas fa-exclamation-triangle"></i>
          Quota Reached Policy
        </h5>
        <div class="options-grid">
          <div class="option-card active-option" 
               *ngFor="let option of getActiveOptions(policyDetails.QuotaReached || [])">
            <div class="option-header">
              <span class="option-status status-active">
                <i class="fas fa-check-circle"></i>
                Active
              </span>
              <span class="option-name">{{ option.optionName }}</span>
            </div>
            <p class="option-description">{{ processDescription(option.description, option.parVal, option.parUnit) }}</p>
            <div class="option-params" *ngIf="option.parVal && option.parUnit">
              <span class="param-badge">{{ option.parVal }} {{ option.parUnit }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Intimations -->
      <div class="policy-section" *ngIf="hasNotificationIntimations()">
        <h5 class="section-title">
          <i class="fas fa-bell"></i>
          Notification Intimations
        </h5>
        
        <!-- VM Policy Notifications (Grid Style) -->
        <div *ngIf="isVMPolicy()" class="notifications-grid">
          <div class="notification-card" *ngFor="let notification of policyDetails.NotificationIntimations">
            <div class="notification-header">
              <span class="percentage-badge">{{ notification.percentage }}%</span>
              <span class="teams-count">{{ notification.teams.length }} Team(s)</span>
            </div>
            <div class="teams-list">
              <span class="team-badge" *ngFor="let team of notification.teams">
                <i class="fas fa-users"></i>
                {{ team.name }}
              </span>
            </div>
          </div>
        </div>

        <!-- Subscription Policy Notifications (Table Style) -->
        <div *ngIf="isSubscriptionPolicy()" class="notification-table-container">
          <div class="table-description">
            <p class="text-muted">Notify only relevant labsupport, SME etc.</p>
          </div>
          <table class="notification-table">
            <thead>
              <tr>
                <th>User Type to Notify</th>
                <th *ngFor="let notification of policyDetails.NotificationIntimations">
                  {{ notification.percentage }}%
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="header-cell">QuotaUsage Percentage</td>
                <td *ngFor="let notification of policyDetails.NotificationIntimations" class="percentage-cell">
                  {{ notification.percentage }}
                </td>
              </tr>
              <tr *ngFor="let teamType of getAllTeamTypes()">
                <td class="team-type-cell">{{ teamType }}</td>
                <td *ngFor="let notification of policyDetails.NotificationIntimations" class="checkbox-cell">
                  <i class="fas fa-check" *ngIf="hasTeamType(notification, teamType)"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>

  <!-- Error State -->
  <div *ngIf="!loading && !policyDetails" class="error-state">
    <i class="fas fa-exclamation-circle"></i>
    <p>Failed to load policy details. Please try again.</p>
  </div>
</div>
